import type { SalaryResultsType, INSSResultType, IRRFResultType, SalarioFamiliaResultType } from "../salario-liquido";

const CONSTANTES_2025 = {
  INSS: {
    TETO_CONTRIBUICAO: 8157.41,
    DECIMAL_PRECISION: 100,
  },
  IRRF: {
    VALOR_DEPENDENTE: 189.59,
  },
  SALARIO_FAMILIA: {
    VALOR_POR_DEPENDENTE: 65.00,
    TETO_RENDA: 1906.04,
  },
  CALCULO: {
    PERCENTAGE_MULTIPLIER: 100,
    MIN_VALUE: 0,
  },
} as const;

// Tabelas oficiais de 2025 - INSS (CORRETAS)
const TABELA_INSS_2025 = [
  { limite: 1518.0, aliquota: 0.075, parcelaDeducao: 0 },
  { limite: 2793.88, aliquota: 0.09, parcelaDeducao: 22.77 },
  { limite: 4190.83, aliquota: 0.12, parcelaDeducao: 106.59 },
  { limite: 8157.41, aliquota: 0.14, parcelaDeducao: 190.4 },
] as const;

// TABELA IRRF 2025 - HOTFIX CRÍTICO APLICADO
// Fonte: Lei nº 14.848/2024 - Receita Federal (gov.br)
const TABELA_IRRF_2025 = [
  { limite: 2259.20, aliquota: 0, parcelaDeducao: 0 },                        
  { limite: 2826.65, aliquota: 0.075, parcelaDeducao: 169.44 },               
  { limite: 3751.05, aliquota: 0.15, parcelaDeducao: 381.44 },                
  { limite: 4664.68, aliquota: 0.225, parcelaDeducao: 662.77 },               
  { limite: Number.POSITIVE_INFINITY, aliquota: 0.275, parcelaDeducao: 896.00 }, 
] as const;

type TabelaImpostoItem = {
  readonly limite: number;
  readonly aliquota: number;
  readonly parcelaDeducao: number;
};

// Utilitários consolidados
const arredondarMoeda = (valor: number): number => 
  Math.round(valor * CONSTANTES_2025.INSS.DECIMAL_PRECISION) / CONSTANTES_2025.INSS.DECIMAL_PRECISION;

const encontrarFaixaImposto = (valor: number, tabela: readonly TabelaImpostoItem[]): TabelaImpostoItem => {
  const faixa = tabela.find(item => valor <= item.limite);
  return faixa || tabela[tabela.length - 1];
};

const calcularPorcentagem = (valor: number, base: number): number => {
  return base > CONSTANTES_2025.CALCULO.MIN_VALUE ? (valor / base) * CONSTANTES_2025.CALCULO.PERCENTAGE_MULTIPLIER : CONSTANTES_2025.CALCULO.MIN_VALUE;
};

export const validarParametrosCalculo = (
  salarioBruto: number, 
  numeroDependentes: number, 
  dependentesMenores14: number
): void => {
  if (salarioBruto <= CONSTANTES_2025.CALCULO.MIN_VALUE) {
    throw new Error("Salário bruto deve ser maior que zero");
  }
  
  if (numeroDependentes < CONSTANTES_2025.CALCULO.MIN_VALUE) {
    throw new Error("Número de dependentes não pode ser negativo");
  }
  
  if (dependentesMenores14 < CONSTANTES_2025.CALCULO.MIN_VALUE) {
    throw new Error("Dependentes menores de 14 anos não pode ser negativo");
  }
  
  if (dependentesMenores14 > numeroDependentes) {
    throw new Error("Dependentes menores de 14 anos não pode ser maior que total de dependentes");
  }
};

/**
 * Calcula o INSS com base na tabela progressiva oficial de 2025
 * Usando a fórmula: (Salário x Alíquota) - Parcela a Deduzir
 */
export const calcularINSS = (salario: number): INSSResultType => {
  if (salario <= CONSTANTES_2025.CALCULO.MIN_VALUE) {
    return { valor: 0, baseCalculo: 0, aliquotaEfetiva: 0 };
  }

  const baseCalculo = Math.min(salario, CONSTANTES_2025.INSS.TETO_CONTRIBUICAO);
  const faixa = encontrarFaixaImposto(baseCalculo, TABELA_INSS_2025);
  
  const valorINSS = Math.max(CONSTANTES_2025.CALCULO.MIN_VALUE, baseCalculo * faixa.aliquota - faixa.parcelaDeducao);
  const aliquotaEfetiva = calcularPorcentagem(valorINSS, baseCalculo);

  return {
    valor: arredondarMoeda(valorINSS),
    baseCalculo: arredondarMoeda(baseCalculo),
    aliquotaEfetiva: arredondarMoeda(aliquotaEfetiva),
  };
};

/**
 * Base de cálculo: Salário Bruto - INSS - (Dependentes x R$ 189,59)
 */
export const calcularIRRF = (salario: number, inss: number, dependentes: number): IRRFResultType => {
  const deducaoDependentes = dependentes * CONSTANTES_2025.IRRF.VALOR_DEPENDENTE;
  const baseCalculo = Math.max(CONSTANTES_2025.CALCULO.MIN_VALUE, salario - inss - deducaoDependentes);

  if (baseCalculo <= CONSTANTES_2025.CALCULO.MIN_VALUE) {
    return { 
      valor: 0, 
      baseCalculo: arredondarMoeda(baseCalculo), 
      deducaoDependentes: arredondarMoeda(deducaoDependentes) 
    };
  }

  const faixa = encontrarFaixaImposto(baseCalculo, TABELA_IRRF_2025);
  const valorIRRF = Math.max(CONSTANTES_2025.CALCULO.MIN_VALUE, baseCalculo * faixa.aliquota - faixa.parcelaDeducao);

  return {
    valor: arredondarMoeda(valorIRRF),
    baseCalculo: arredondarMoeda(baseCalculo),
    deducaoDependentes: arredondarMoeda(deducaoDependentes),
  };
};

/**
 * Verifica se tem direito ao salário-família
 * Direito: salário até R$ 1.906,04 e ter dependentes menores de 14 anos
 * Valor: R$ 65,00 por dependente (Portaria MPS/MF nº 6/2025)
 */
export const verificarSalarioFamilia = (salarioBruto: number, dependentesMenores14: number): SalarioFamiliaResultType => {
  const temDireito = dependentesMenores14 > CONSTANTES_2025.CALCULO.MIN_VALUE && 
                    salarioBruto <= CONSTANTES_2025.SALARIO_FAMILIA.TETO_RENDA;
  
  if (temDireito) {
    const valorTotal = dependentesMenores14 * CONSTANTES_2025.SALARIO_FAMILIA.VALOR_POR_DEPENDENTE;
    
    return {
      temDireito: true,
      valor: arredondarMoeda(valorTotal),
      quantidadeDependentes: dependentesMenores14,
      observacao: gerarObservacaoSalarioFamilia(dependentesMenores14, valorTotal),
    };
  }

  return {
    temDireito: false,
    valor: 0,
    quantidadeDependentes: 0,
    observacao: gerarObservacaoSemDireito(salarioBruto, dependentesMenores14),
  };
};

const gerarObservacaoSalarioFamilia = (dependentes: number, valorTotal: number): string => {
  const valorFormatado = CONSTANTES_2025.SALARIO_FAMILIA.VALOR_POR_DEPENDENTE.toFixed(2).replace('.', ',');
  const totalFormatado = valorTotal.toFixed(2).replace('.', ',');
  
  return `Você tem direito ao salário-família de R$ ${valorFormatado} por dependente menor de 14 anos. Total: ${dependentes} dependente(s) = R$ ${totalFormatado}.`;
};

const gerarObservacaoSemDireito = (salario: number, dependentes: number): string | null => {
  if (salario > CONSTANTES_2025.SALARIO_FAMILIA.TETO_RENDA && dependentes > CONSTANTES_2025.CALCULO.MIN_VALUE) {
    const tetoFormatado = CONSTANTES_2025.SALARIO_FAMILIA.TETO_RENDA.toFixed(2).replace('.', ',');
    return `Salário acima de R$ ${tetoFormatado}. Sem direito ao salário-família.`;
  }
  return null;
};

const calcularDescontosTotais = (
  inssValor: number, 
  irrfValor: number, 
  pensaoAlimenticia: number, 
  outrosDescontos: number
): number => {
  return inssValor + irrfValor + pensaoAlimenticia + outrosDescontos;
};

const calcularSalarioFinal = (
  salarioBruto: number, 
  totalDescontos: number, 
  beneficios: number
): number => {
  return salarioBruto - totalDescontos + beneficios;
};

const calcularPercentualLiquido = (salarioLiquido: number, salarioBruto: number): number => {
  return calcularPorcentagem(salarioLiquido, salarioBruto);
};

const construirResultado = (
  salarioBruto: number,
  inssResult: INSSResultType,
  irrfResult: IRRFResultType,
  salarioFamiliaResult: SalarioFamiliaResultType,
  pensaoAlimenticia: number,
  outrosDescontos: number
): SalaryResultsType => {
  const totalDescontos = calcularDescontosTotais(
    inssResult.valor, 
    irrfResult.valor, 
    pensaoAlimenticia, 
    outrosDescontos
  );
  
  const salarioLiquido = calcularSalarioFinal(
    salarioBruto, 
    totalDescontos, 
    salarioFamiliaResult.valor
  );
  
  const percentualLiquido = calcularPercentualLiquido(salarioLiquido, salarioBruto);

  return {
    salarioBruto: arredondarMoeda(salarioBruto),
    inss: inssResult,
    irrf: irrfResult,
    pensaoAlimenticia: arredondarMoeda(pensaoAlimenticia),
    outrosDescontos: arredondarMoeda(outrosDescontos),
    salarioFamilia: salarioFamiliaResult,
    totalDescontos: arredondarMoeda(totalDescontos),
    salarioLiquido: arredondarMoeda(salarioLiquido),
    percentualLiquido: arredondarMoeda(percentualLiquido),
  };
};

/**
 * Executa o cálculo completo do salário líquido
 */
export const calcularSalarioLiquido = (
  salarioBruto: number,
  numeroDependentes: number,
  dependentesMenores14: number,
  pensaoAlimenticia: number,
  outrosDescontos: number
): SalaryResultsType => {
  // Validação consolidada reutilizada
  validarParametrosCalculo(salarioBruto, numeroDependentes, dependentesMenores14);

  // Cálculos individuais com tabelas corrigidas
  const inssResult = calcularINSS(salarioBruto);
  const irrfResult = calcularIRRF(salarioBruto, inssResult.valor, numeroDependentes);
  const salarioFamiliaResult = verificarSalarioFamilia(salarioBruto, dependentesMenores14);

  // Construção do resultado final
  return construirResultado(
    salarioBruto,
    inssResult,
    irrfResult,
    salarioFamiliaResult,
    pensaoAlimenticia,
    outrosDescontos
  );
};